import { z } from 'zod';

/**
 * Canonical password requirements for sign-up and password changes.
 *
 * These MUST mirror the Supabase GoTrue project config (Auth → Policies).
 * TARGET policy (BAL-716, relaxed 2026-07-10 for the GDO registration flood —
 * strict rules were turning parents away):
 *   password_min_length          = 8
 *   password_required_characters = ""     (NO uppercase/lowercase/digit/symbol)
 *   password_hibp_enabled        = false  (no breach check)
 *
 * The live GoTrue enforcement lives in the Supabase Management API, not in this
 * file. `apps/web/supabase/config.prod.toml` mirrors the same values so a
 * `config push` can't silently regress them, but the Management API PATCH
 * (password_min_length=8, password_required_characters="") is the source of
 * truth for enforcement and is applied at deploy time — keep the two in sync.
 *
 * The `passwordRequirements` array below is the single source of truth for the
 * CLIENT: the zod refinement (`validatePassword`) AND the live requirements
 * checklist (`PasswordRequirementsChecklist`) both read from it, so what the
 * user SEES can never drift from what the client validates. `password.schema
 * .test.ts` asserts this list matches the intended server policy (min length
 * only, no character classes) so a future change to one without the other
 * fails CI. If the GoTrue policy changes, update this list, the config.prod
 * .toml values, AND the Management API — all three must agree.
 */
export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 99;

export interface PasswordRequirement {
  /** Stable id — React key, test hook, and zod issue discriminator. */
  id: string;
  /** i18n key rendered in the checklist (under `auth:passwordChecklist.*`). */
  i18nKey: string;
  /** Optional interpolation values for the checklist string (e.g. min length). */
  i18nValues?: Record<string, number>;
  /** Returns true when `password` satisfies this rule. */
  isMet: (password: string) => boolean;
}

export const passwordRequirements: PasswordRequirement[] = [
  {
    id: 'minLength',
    i18nKey: 'auth:passwordChecklist.minLength',
    i18nValues: { count: PASSWORD_MIN_LENGTH },
    isMet: (password) => password.length >= PASSWORD_MIN_LENGTH,
  },
  // No character-class requirement (BAL-716). GoTrue's
  // password_required_characters is empty, so the client must not promise or
  // enforce uppercase/lowercase/digit/symbol rules the server doesn't apply.
];

/**
 * Loose schema for SIGN IN — validates existing passwords only. Kept lenient
 * (length bounds only) so users whose passwords predate the current policy are
 * never blocked at login by a client rule the server doesn't apply on sign-in.
 */
export const PasswordSchema = z
  .string()
  .min(8, {
    error: 'auth:errors.minPasswordLength',
  })
  .max(PASSWORD_MAX_LENGTH);

/**
 * Strict schema for SIGN UP and password change. Enforces every rule in
 * `passwordRequirements` so the client agrees with GoTrue before submit. A
 * single generic issue is raised when anything is unmet — the live checklist
 * shows which specific rules are failing, so a per-rule message under the field
 * would be redundant noise.
 */
export const RefinedPasswordSchema = z
  .string()
  .max(PASSWORD_MAX_LENGTH)
  .superRefine((val, ctx) => {
    validatePassword(val, ctx);
  });

export function refineRepeatPassword(
  data: { password: string; repeatPassword: string },
  ctx: z.RefinementCtx,
) {
  if (data.password !== data.repeatPassword) {
    ctx.addIssue({
      message: 'auth:errors.passwordsDoNotMatch',
      path: ['repeatPassword'],
      code: 'custom',
    });
  }

  return true;
}

function validatePassword(password: string, ctx: z.RefinementCtx) {
  const allMet = passwordRequirements.every((req) => req.isMet(password));

  if (!allMet) {
    ctx.addIssue({
      code: 'custom',
      message: 'auth:errors.passwordRequirementsNotMet',
    });
  }

  return true;
}
